// Thin client for the Django REST Framework backend. Falls back silently to
// localStorage-only mode (handled in store.tsx) if NEXT_PUBLIC_API_BASE_URL
// is unset — so the UI never hard-fails a demo. Once a real user has
// registered/logged in, their own token is used for every request; the demo
// service account (see .env.local.example) is only a fallback for anonymous
// browsing before sign-in.

export const API_BASE = process.env.NEXT_PUBLIC_API_BASE_URL ?? ""

function getToken(key: string): string | null {
  if (typeof window === "undefined") return null;
  return window.localStorage.getItem(key);
}
function setTokens(access: string, refresh: string) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem("uas_access_token", access);
  window.localStorage.setItem("uas_refresh_token", refresh);
}
export function clearTokens() {
  if (typeof window === "undefined") return;
  window.localStorage.removeItem("uas_access_token");
  window.localStorage.removeItem("uas_refresh_token");
  window.localStorage.removeItem("uas_is_demo_session");
}
export function isLoggedIn(): boolean {
  return (
    !!getToken("uas_access_token") && getToken("uas_is_demo_session") !== "true"
  );
}

async function rawLogin(
  username: string,
  password: string,
  isDemo = false,
): Promise<boolean> {
  try {
    const res = await fetch(`${API_BASE}/auth/token/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ username, password }),
    });
    if (!res.ok) return false;
    const data = await res.json();
    setTokens(data.access, data.refresh);
    if (typeof window !== "undefined")
      window.localStorage.setItem("uas_is_demo_session", String(isDemo));
    return true;
  } catch {
    return false;
  }
}

async function refreshAccessToken(): Promise<boolean> {
  const refresh = getToken("uas_refresh_token");
  if (!refresh) return false;
  try {
    const res = await fetch(`${API_BASE}/auth/token/refresh/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refresh }),
    });
    if (!res.ok) return false;
    const data = await res.json();
    if (typeof window !== "undefined")
      window.localStorage.setItem("uas_access_token", data.access);
    return true;
  } catch {
    return false;
  }
}

/** Ensures a usable access token exists. Prefers a real logged-in user's
 * token; only falls back to the demo account (flagged as a demo session so
 * the UI can tell the difference) when nobody is signed in yet. */
export async function ensureAuth(): Promise<boolean> {
  if (!API_BASE) return false;
  return !!getToken("uas_access_token");
}

export async function login(username: string, password: string) {
  return rawLogin(username, password, false);
}

export function logout() {
  clearTokens();
}

/** Generic authenticated JSON request. Returns null (never throws) on any
 * failure — network error, 401, validation error — so callers degrade to
 * local-only state instead of crashing the UI. Pass onError to inspect why. */
export async function apiRequest<T>(
  path: string,
  options: RequestInit = {},
  onError?: (status: number, body: unknown) => void,
): Promise<T | null> {
  if (!API_BASE) return null;
  await ensureAuth();

  const doFetch = async () => {
    const token = getToken("uas_access_token");
    return fetch(`${API_BASE}${path}`, {
      ...options,
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(options.headers ?? {}),
      },
    });
  };

  try {
    let res = await doFetch();
    if (res.status === 401) {
      const refreshed = await refreshAccessToken();
      if (refreshed) res = await doFetch();
    }
    if (!res.ok) {
      let body: unknown = null;
      try {
        body = await res.json();
      } catch {
        /* non-JSON error body */
      }
      onError?.(res.status, body);
      console.error(
        `[uas-portal] API ${options.method ?? "GET"} ${path} -> ${res.status}`,
        body,
      );
      return null;
    }
    if (res.status === 204) return null;
    return (await res.json()) as T;
  } catch (err) {
    console.error(
      `[uas-portal] API ${options.method ?? "GET"} ${path} failed`,
      err,
    );
    return null;
  }
}

/** Same contract as apiRequest, but sends a FormData body (multipart) for
 * endpoints that accept a file upload alongside ordinary fields. Never set
 * Content-Type manually here — the browser must generate the multipart
 * boundary itself. */
export async function apiRequestMultipart<T>(
  path: string,
  method: "POST" | "PATCH" | "PUT",
  formData: FormData,
  onError?: (status: number, body: unknown) => void,
): Promise<T | null> {
  if (!API_BASE) return null;
  await ensureAuth();

  const doFetch = async () => {
    const token = getToken("uas_access_token");
    return fetch(`${API_BASE}${path}`, {
      method,
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: formData,
    });
  };

  try {
    let res = await doFetch();
    if (res.status === 401) {
      const refreshed = await refreshAccessToken();
      if (refreshed) res = await doFetch();
    }
    if (!res.ok) {
      let body: unknown = null;
      try {
        body = await res.json();
      } catch {
        /* non-JSON error body */
      }
      onError?.(res.status, body);
      console.error(
        `[uas-portal] API ${method} ${path} -> ${res.status}`,
        body,
      );
      return null;
    }
    return (await res.json()) as T;
  } catch (err) {
    console.error(`[uas-portal] API ${method} ${path} failed`, err);
    return null;
  }
}

/** GET a list endpoint filtered by DRF's `search` query param — used to
 * resolve foreign keys (owner CNIC -> id, UAS serial -> id) before posting
 * dependent records like transfers or SDUC requests. */
export async function apiSearch<T>(path: string, search: string): Promise<T[]> {
  const res = await apiRequest<{ results?: T[] } | T[]>(
    `${path}?search=${encodeURIComponent(search)}`,
  );
  if (!res) return [];
  return Array.isArray(res) ? res : (res.results ?? []);
}
